import React, {useEffect, useState} from 'react'
import _ from 'lodash'
import AsyncSelect from 'react-select/async'
import PropTypes from 'prop-types'
import debounce from 'debounce-promise'
import {fetchGroups} from '../../utils/services'
import PapiGroup from './PapiGroup'
import LoadingIndicator from '../shared/LoadingIndicator'
import DropdownIndicator from '../shared/DropdownIndicator'
import FloatingLabel from '../shared/FloatingLabel'
import useFocussedPlaceholder from '../../utils/useFocussedPlaceholder'

import {CLASSNAME, CLASSNAME_PREFIX, componentClassName, isRequiredClassName} from '../constants'
import {getCachedGroup, waitForCachedGroup, waitForCachedGroups} from './groupCache'

const fetchMatchingItems = (
    nonSelectableItems,
    hideUnrequestable,
    isMember,
    searchCallback,
    customFields,
    tokenProvider,
    profileApiUrl
) =>
    debounce(
        (input) =>
            fetchGroups(
                input,
                nonSelectableItems,
                hideUnrequestable,
                isMember,
                customFields,
                tokenProvider,
                profileApiUrl
            ).then((options) => Promise.resolve(searchCallback(options))),
        300
    )

const renderItem = (group, size, showLink, showMemberCount, tokenProvider, profileApiUrl) => {
    const getRenderGroup = () => {
        if (group.name) {
            return group
        }
        return getCachedGroup(group.id, tokenProvider, profileApiUrl)
    }
    const renderGroup = getRenderGroup()
    if (renderGroup) {
        return (
            <PapiGroup
                group={renderGroup}
                size={size}
                showLink={showLink}
                showMemberCount={showMemberCount}
                tokenProvider={tokenProvider}
                profileApiUrl={profileApiUrl}
            />
        )
    }
    return (
        <PapiGroup
            id={group.id}
            size={size}
            showLink={showLink}
            showMemberCount={showMemberCount}
            tokenProvider={tokenProvider}
            profileApiUrl={profileApiUrl}
        />
    )
}

const renderLabel =
    (size, showLink, showMemberCount, tokenProvider, profileApiUrl) =>
    ({children, data}) =>
        children.props.group
            ? renderItem(
                  children.props.group,
                  size,
                  showLink,
                  showMemberCount,
                  tokenProvider,
                  profileApiUrl
              )
            : renderItem(
                  {id: data.id},
                  size,
                  showLink,
                  showMemberCount,
                  tokenProvider,
                  profileApiUrl
              )

const PapiGroupSelect = (props) => {
    const {
        title,
        outlined,
        value,
        nonSelectableItems = [],
        hideUnrequestable = false,
        isMember = false,
        isRequired,
        showLink = false,
        showMemberCount = false,
        showOptionLink = false,
        size = 'medium',
        searchCallback = (items) => items,
        customFields,
        tokenProvider,
        profileApiUrl
    } = props
    const [originalPlaceholder] = useState(props.placeholder)

    useEffect(() => {
        const hydrateValues = async () => {
            if (value) {
                if (Array.isArray(value)) {
                    const [full, needQuerying] = _.partition(value, (val) => !!val.name)
                    if (!_.isEmpty(needQuerying)) {
                        const loaded = await waitForCachedGroups(
                            needQuerying.map((val) => val.id),
                            tokenProvider,
                            profileApiUrl
                        )
                        props.onChange(full.concat(loaded))
                    }
                } else if (!value.name) {
                    props.onChange(await waitForCachedGroup(value.id, tokenProvider, profileApiUrl))
                }
            }
        }
        hydrateValues().catch((err) =>
            console.error('Error occurred hydrating group values: ', err)
        )
    }, [JSON.stringify(value)])

    const {isFocussed, placeholder, handleOnFocus, handleOnBlur} = useFocussedPlaceholder(
        false,
        originalPlaceholder || title,
        isRequired
    )

    const determineDisplayValues = () => {
        if (Array.isArray(value)) {
            return value.map((val) => (val.name ? val : {id: val.id, name: val.id}))
        }
        return !value || value?.name ? value : {id: value.id, name: value.id}
    }
    return (
        <div className={`papi-react ${isRequiredClassName({isRequired, value})}`}>
            <div className={componentClassName(outlined)}>
                <FloatingLabel
                    title={title}
                    value={value}
                    isFocussed={isFocussed}
                    isRequired={isRequired}
                    hasCustomPlaceholder={!!originalPlaceholder}
                />
                <AsyncSelect
                    id="group-select"
                    className={CLASSNAME}
                    classNamePrefix={CLASSNAME_PREFIX}
                    placeholder={placeholder}
                    getOptionLabel={(option) =>
                        renderItem(option, size, showOptionLink, showMemberCount)
                    }
                    getOptionValue={(option) => option.id}
                    defaultOptions={isMember || []}
                    loadOptions={fetchMatchingItems(
                        nonSelectableItems,
                        hideUnrequestable,
                        isMember,
                        searchCallback,
                        customFields,
                        tokenProvider,
                        profileApiUrl
                    )}
                    openMenuOnFocus={false}
                    openMenuOnClick={false}
                    isClearable
                    onFocus={handleOnFocus}
                    onBlur={handleOnBlur}
                    cacheOptions
                    closeMenuOnSelect
                    components={{
                        LoadingIndicator,
                        SingleValue: renderLabel(size, showLink, showMemberCount),
                        MultiValueLabel: renderLabel(size, showLink, showMemberCount),
                        DropdownIndicator
                    }}
                    {...props}
                    value={determineDisplayValues()}
                />
            </div>
        </div>
    )
}

PapiGroupSelect.displayName = 'PapiGroupSelect'

PapiGroupSelect.propTypes = {
    title: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    value: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    outlined: PropTypes.bool,
    isRequired: PropTypes.bool,
    isMulti: PropTypes.bool,
    hideUnrequestable: PropTypes.bool,
    isMember: PropTypes.bool,
    showLink: PropTypes.bool,
    showMemberCount: PropTypes.bool,
    showOptionLink: PropTypes.bool,
    nonSelectableItems: PropTypes.arrayOf(PropTypes.shape({id: PropTypes.string})),
    size: PropTypes.oneOf(['small', 'medium', 'large', 'x-large']),
    searchCallback: PropTypes.func,
    customFields: PropTypes.string
}

export default PapiGroupSelect
